import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { useTheme } from 'context/ThemeContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import type { ComingObjectOption } from '../model/comingObject';
import { ALL_COMING_OBJECTS_KEY, toComingObjectKey } from '../model/comingObject';

interface ObjectSelectOption {
    value: number;
    label: string;
    title: string;
    subtitle?: string;
    remaining: number;
}

interface ComingObjectSelectProps {
    options: ComingObjectOption[];
    selectedKey: number;
    disabled?: boolean;
    onSelect: (key: number) => void;
}

const ComingObjectSelect = ({
    options,
    selectedKey,
    disabled = false,
    onSelect,
}: ComingObjectSelectProps) => {
    const { t } = useTranslation(['Vendors']);
    const { darkTheme } = useTheme();
    const [selectTheme, setSelectTheme] = useState(darkTheme ? selectStylesDark : selectStylesLigth);

    useEffect(() => {
        setSelectTheme(darkTheme ? selectStylesDark : selectStylesLigth);
    }, [darkTheme]);

    const selectOptions = useMemo<ObjectSelectOption[]>(() => {
        const allRemaining = options.reduce((sum, option) => sum + option.remaining, 0);
        const allTitle = t('supplierRequests.view.allObjects');
        return [
            {
                value: ALL_COMING_OBJECTS_KEY,
                label: allTitle,
                title: allTitle,
                remaining: allRemaining,
            },
            ...options.map((option) => {
                const title = option.objectId
                    ? option.objectName || option.clientName || `#${option.objectId}`
                    : t('supplierRequests.view.noObject');
                const subtitle =
                    option.objectName &&
                    option.clientName &&
                    option.objectName !== option.clientName
                        ? option.clientName
                        : Number(option.onlySale)
                          ? t('supplierRequests.create.modeSale')
                          : undefined;
                return {
                    value: toComingObjectKey(option.objectId),
                    label: [title, subtitle].filter(Boolean).join(' '),
                    title,
                    subtitle,
                    remaining: option.remaining,
                };
            }),
        ];
    }, [options, t]);

    const value =
        selectOptions.find((option) => option.value === selectedKey) || selectOptions[0] || null;

    const styles = {
        ...selectTheme,
        menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 1060 }),
        option: (base: Record<string, unknown>, state: unknown) => ({
            ...base,
            ...(selectTheme.option ? selectTheme.option(base, state as never) : {}),
            paddingTop: 8,
            paddingBottom: 8,
        }),
    };

    return (
        <div className="mb-3">
            <label className="form-label text-muted">
                {t('supplierRequests.view.selectObject')}
            </label>
            <Select<ObjectSelectOption>
                options={selectOptions}
                value={value}
                isDisabled={disabled}
                isClearable={false}
                isSearchable={selectOptions.length > 6}
                styles={styles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                placeholder={t('supplierRequests.view.selectObject')}
                formatOptionLabel={(option) => (
                    <span className="d-flex align-items-center justify-content-between gap-2 min-w-0">
                        <div className="min-w-0 overflow-hidden flex-grow-1">
                            <h6 className="my-0 bg-error fs-14 fw-medium text-truncate" title={option.title}>
                                {option.title}
                            </h6>
                            {option.subtitle ? (
                                <p
                                    className="text-muted f-12 mb-0 text-truncate"
                                    title={option.subtitle}
                                >
                                    {option.subtitle}
                                </p>
                            ) : null}
                        </div>
                        {option.remaining > 0 ? (
                            <span className="flex-shrink-0 fs-12 text-muted">
                                +{option.remaining}
                            </span>
                        ) : null}
                    </span>
                )}
                onChange={(option) => {
                    if (option) onSelect(option.value);
                }}
            />
        </div>
    );
};

export default ComingObjectSelect;
